import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { T, FONT_STACK, NUMERIC, money } from '../containers/customer/customers.theme';
import { grouped } from '../containers/dashboard/dashboard.theme';
import {
  DashboardKeyframes,
  DeltaChip,
  EmptyPanel,
  HeroTile,
  Panel,
  Skeleton,
  StatTile,
} from '../containers/dashboard/DashboardUI';
import RevenueTrend from '../containers/dashboard/RevenueTrend';
import AttentionStrip from '../containers/dashboard/AttentionStrip';
import {
  CategoryPanel,
  OrderTypePanel,
  PaymentPanel,
  WeekdayPanel,
} from '../containers/dashboard/DashboardPanels';
import {
  LatestOrders,
  NotMovingPanel,
  SellingPanel,
  StaffPanel,
} from '../containers/dashboard/DashboardLists';

import { fetchCanceledOrders, fetchOrders } from '../containers/dashboard/dashboardApi';
import { fetchMenu } from '../containers/menu/menuApi';
import { setCategories, setCategoryDataFetched } from '../containers/orders/categorySlice';
import { setProductDataFetched, setProducts } from '../containers/orders/productSlice';
import {
  PERIODS,
  describePeriod,
  fetchWindow,
  resolvePeriod,
  trendWindow,
} from '../containers/dashboard/dashboard.periods';
import {
  categoryTotals,
  dailyTotals,
  delta,
  latestOrders,
  notMoving,
  orderMoment,
  orderTypeTotals,
  paymentSplit,
  stalledOrders,
  staffTotals,
  summarise,
  topItems,
  weekdayAverages,
} from '../containers/dashboard/dashboard.selectors';

/**
 * The dashboard.
 *
 * One period picker at the top; every panel below reads the same window. The
 * panels do not change when the period does — a manager who learns the layout
 * on Today should find the same screen on This month — except for the two that
 * are meaningless over a single day (weekday averages, and what has not moved),
 * which appear once the period covers more than one.
 *
 * Everything is worked out on the till from orders the API already returns
 * whole. There is no reports endpoint for a daily trend, an item mix or a staff
 * split, and the one that does exist for revenue disagrees with itself about
 * which order statuses count — so this screen asks a question it can trust and
 * does the arithmetic itself, in `dashboard.selectors.js`, where it is tested.
 */

/** How long a ticket may sit before it is worth mentioning. */
const STALLED_MINUTES = 45;

/**
 * Copy with an apostrophe in it lives here rather than inline in the markup:
 * a bare apostrophe in JSX text reads as a quote to most tooling, and has
 * broken this build twice already.
 */
const COPY = {
  notForWaiters: "The dashboard is not part of a waiter's till.",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const activeUser = useSelector((state) => state.login.activeUser);
  const { business } = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);
  const { products, isProductsDataFetched } = useSelector((state) => state.products);
  const { categories, isCategoryDataFetched } = useSelector((state) => state.categories);

  const [periodId, setPeriodId] = React.useState('today');
  const [custom, setCustom] = React.useState({
    start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    end: moment().format('YYYY-MM-DD'),
  });
  const [basis, setBasis] = React.useState('quantity');

  const [orders, setOrders] = React.useState([]);
  const [previous, setPrevious] = React.useState([]);
  const [canceled, setCanceled] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loadedAt, setLoadedAt] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  const currency = business?.currencyMark || '$';
  const isManager = activeUser?.role === 'manager';
  const isWaiter = activeUser?.role === 'waiter';

  const period = React.useMemo(() => resolvePeriod(periodId, custom), [periodId, custom]);
  // Not named `window`: shadowing the global is a trap waiting for someone.
  const range = React.useMemo(() => fetchWindow(period), [period]);
  const trend = React.useMemo(() => trendWindow(period), [period]);

  /* ── fetching ───────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (!business?._id || isWaiter) return;

    let cancelled = false;
    const hadData = orders.length > 0;
    if (hadData) setRefreshing(true);
    else setLoading(true);
    setError('');

    Promise.all([
      fetchOrders({ businessId: business._id, start: range.start, end: range.end, token }),
      fetchOrders({
        businessId: business._id,
        start: period.previous.start,
        end: period.previous.end,
        token,
      }),
      // Cancellations are a manager-only endpoint. A cashier simply does not
      // get that line rather than an error where a figure should be.
      isManager
        ? fetchCanceledOrders({
            businessId: business._id,
            start: period.start,
            end: period.end,
            token,
          }).catch(() => [])
        : Promise.resolve([]),
    ])
      .then(([current, before, voided]) => {
        if (cancelled) return;
        setOrders(current);
        setPrevious(before);
        setCanceled(voided);
        setLoadedAt(moment());
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load the dashboard');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
    // `orders.length` is deliberately not a dependency: it is read only to
    // decide between a skeleton and a soft refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, token, isWaiter, isManager, range.start, range.end, period.previous.start, period.previous.end, period.start, period.end, nonce]);

  /**
   * The menu, if nobody has loaded it yet.
   *
   * An order line stores the item id and nothing about its category, so the
   * category split and the "not moving" list are both joins against the menu.
   * It is normally already in the store from the order pad; opening the
   * dashboard first would otherwise show every line as uncategorised.
   */
  React.useEffect(() => {
    if (!business?._id || isWaiter) return;
    if (isProductsDataFetched && isCategoryDataFetched) return;

    let cancelled = false;
    fetchMenu({ businessId: business._id, token })
      .then(({ menuItems, categories: rows }) => {
        if (cancelled) return;
        dispatch(setProducts(menuItems));
        dispatch(setProductDataFetched(true));
        dispatch(setCategories(rows));
        dispatch(setCategoryDataFetched(true));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [business, token, isWaiter, isProductsDataFetched, isCategoryDataFetched, dispatch]);

  /* ── the arithmetic ─────────────────────────────────────────────────── */

  const inPeriod = React.useMemo(() => {
    const start = moment(period.start).startOf('day');
    const end = moment(period.end).endOf('day');
    // The fetch window is wider than the period (it also feeds the chart, and
    // carries a day of slack), so the period's own figures are cut here — by
    // `created_at`, the only timestamp the server writes itself.
    return orders.filter((order) => {
      const at = orderMoment(order);
      return at.isSameOrAfter(start) && at.isSameOrBefore(end);
    });
  }, [orders, period.start, period.end]);

  const summary = React.useMemo(() => summarise(inPeriod), [inPeriod]);
  const previousSummary = React.useMemo(() => summarise(previous), [previous]);

  const trendDays = React.useMemo(
    () => dailyTotals(orders, trend.start, trend.end),
    [orders, trend.start, trend.end]
  );
  const periodDays = React.useMemo(
    () => dailyTotals(inPeriod, period.start, period.end),
    [inPeriod, period.start, period.end]
  );

  const revenueDelta = delta(summary.revenue, previousSummary.revenue);
  const ordersDelta = delta(summary.orders, previousSummary.orders);

  const selling = React.useMemo(() => topItems(inPeriod, { by: basis, limit: 7 }), [inPeriod, basis]);
  const idle = React.useMemo(() => notMoving(inPeriod, products), [inPeriod, products]);
  const byCategory = React.useMemo(
    () => categoryTotals(inPeriod, products, categories),
    [inPeriod, products, categories]
  );
  const byType = React.useMemo(() => orderTypeTotals(inPeriod), [inPeriod]);
  const payment = React.useMemo(() => paymentSplit(inPeriod), [inPeriod]);
  const staff = React.useMemo(() => staffTotals(inPeriod), [inPeriod]);
  const feed = React.useMemo(() => latestOrders(inPeriod, 7), [inPeriod]);
  const weekdays = React.useMemo(() => weekdayAverages(periodDays), [periodDays]);

  // Only meaningful while the period includes right now.
  const stalled = React.useMemo(
    () =>
      moment(period.end).isSame(moment(), 'day')
        ? stalledOrders(inPeriod, STALLED_MINUTES)
        : [],
    [inPeriod, period.end]
  );

  const canceledValue = canceled.reduce((sum, order) => sum + Number(order.total || 0), 0);
  const perDay = period.days > 1 ? summary.revenue / period.days : 0;

  /* ── not for waiters ────────────────────────────────────────────────── */

  if (isWaiter) {
    return (
      <Centered>
        <span style={{ fontSize: 14, color: T.inkSoft }}>{COPY.notForWaiters}</span>
      </Centered>
    );
  }

  const shell = {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    height: 'calc(100vh - 90px)',
    padding: '0 28px 28px',
    background: T.canvas,
    color: T.ink,
    fontFamily: FONT_STACK,
    overflowY: 'auto',
    boxSizing: 'border-box',
  };

  /* ── header, shown in every state so the picker never disappears ────── */

  const header = (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 24,
        flexWrap: 'wrap',
        padding: '20px 0 4px',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.2 }}>Dashboard</span>
        <span style={{ fontSize: 12.5, color: T.inkFaint }}>
          {business?.businessName ? `${business.businessName} · ` : ''}
          {describePeriod(period)}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {periodId === 'custom' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DateInput
              value={custom.start}
              onChange={(value) => setCustom((range) => ({ ...range, start: value }))}
            />
            <span style={{ fontSize: 12, color: T.inkFaint }}>to</span>
            <DateInput
              value={custom.end}
              onChange={(value) => setCustom((range) => ({ ...range, end: value }))}
            />
          </div>
        ) : null}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: 3,
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusPill,
          }}
        >
          {PERIODS.map((option) => {
            const active = option.id === periodId;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setPeriodId(option.id)}
                style={{
                  padding: '7px 15px',
                  background: active ? T.brand : 'transparent',
                  border: 'none',
                  borderRadius: T.radiusPill,
                  font: 'inherit',
                  fontSize: 12.5,
                  fontWeight: active ? 700 : 600,
                  color: active ? T.surface : T.inkSoft,
                  cursor: 'pointer',
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => setNonce((value) => value + 1)}
          title="Refresh"
          style={{
            padding: 0,
            background: 'transparent',
            border: 'none',
            font: 'inherit',
            fontSize: 11.5,
            color: T.inkFaint,
            cursor: 'pointer',
          }}
        >
          {refreshing ? 'Refreshing…' : loadedAt ? `Updated ${loadedAt.format('h:mm A')}` : ''}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={shell}>
        <DashboardKeyframes />
        {header}
        <div style={{ display: 'grid', gridTemplateColumns: '1.75fr 1fr 1fr 1fr', gap: 14 }}>
          {[0, 1, 2, 3].map((index) => (
            <Panel key={index} style={{ gap: 14, height: 132 }}>
              <Skeleton width="45%" height={12} />
              <Skeleton width="70%" height={index === 0 ? 34 : 24} />
              <Skeleton width="55%" height={10} />
            </Panel>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14 }}>
          <Panel style={{ height: 300 }}><Skeleton height={240} radius={8} /></Panel>
          <Panel style={{ height: 300 }}><Skeleton height={240} radius={8} /></Panel>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={shell}>
        {header}
        <Panel>
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <span style={{ fontSize: 14, color: T.danger }}>{error}</span>
          </div>
        </Panel>
      </div>
    );
  }

  if (!summary.orders) {
    return (
      <div style={shell}>
        <DashboardKeyframes />
        {header}
        <Panel>
          <EmptyPanel
            title={`No orders ${period.isSingleDay ? 'yet' : 'in this period'}`}
            note={
              previousSummary.revenue
                ? `${describeComparison(period)} finished at ${money(previousSummary.revenue, currency)} across ${grouped(previousSummary.orders)} orders.`
                : null
            }
          />
        </Panel>
      </div>
    );
  }

  return (
    <div style={shell}>
      <DashboardKeyframes />
      {header}

      {/* Held at reduced opacity while a new period loads, so nothing jumps. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          opacity: refreshing ? 0.55 : 1,
          transition: 'opacity 120ms linear',
        }}
      >
        {/* ── the four numbers ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.75fr 1fr 1fr 1fr', gap: 14 }}>
          <HeroTile
            label={period.isSingleDay ? 'Revenue' : 'Revenue in this period'}
            value={money(summary.revenue, currency)}
            delta={<DeltaChip delta={revenueDelta} />}
            footnote={
              <>
                vs {money(previousSummary.revenue, currency)} {period.previous.label}
                {period.days > 1 ? (
                  <>
                    <br />
                    Averaging {money(perDay, currency)} a day
                  </>
                ) : null}
              </>
            }
          />

          <StatTile
            label="Orders"
            value={grouped(summary.orders)}
            accent={
              ordersDelta.percent !== null && ordersDelta.direction !== 'flat' ? (
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: ordersDelta.direction === 'down' ? T.danger : T.paid,
                    ...NUMERIC,
                  }}
                >
                  {ordersDelta.amount > 0 ? '+' : ''}
                  {ordersDelta.amount}
                </span>
              ) : null
            }
            note={
              <>
                Average ticket{' '}
                <strong style={{ color: T.inkSoft, fontWeight: 700, ...NUMERIC }}>
                  {money(summary.average, currency)}
                </strong>
              </>
            }
          />

          <StatTile
            label="Open tickets"
            value={grouped(summary.openCount)}
            accent={
              summary.openValue > 0 ? (
                <span style={{ fontSize: 15, fontWeight: 700, color: T.pendingInk, ...NUMERIC }}>
                  {money(summary.openValue, currency)}
                </span>
              ) : null
            }
            note="Placed but not paid yet"
          />

          <StatTile
            label="On account"
            value={money(summary.invoiceValue, currency)}
            note={
              summary.invoiceCount
                ? `${grouped(summary.invoiceCount)} invoice ${
                    summary.invoiceCount === 1 ? 'order' : 'orders'
                  }, added to customer balances`
                : 'Nothing went on the book'
            }
          />
        </div>

        <AttentionStrip
          stalled={stalled}
          stalledMinutes={STALLED_MINUTES}
          summary={summary}
          canceled={canceled.length}
          canceledValue={canceledValue}
          menuSold={idle.soldCount}
          menuTotal={idle.total}
          currency={currency}
          showCancellations={isManager}
          isSingleDay={period.isSingleDay}
        />

        {/* ── the trend, and what is selling ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14 }}>
          <Panel
            title={`Revenue, ${trend.label}`}
            subtitle={
              period.isSingleDay
                ? 'Today in context — the days around it, for scale'
                : `Every day in the period`
            }
          >
            <RevenueTrend
              days={trendDays}
              currency={currency}
              todayKey={period.end}
              height={212}
            />
          </Panel>

          <SellingPanel
            rows={selling}
            currency={currency}
            basis={basis}
            onBasis={setBasis}
            subtitle={basis === 'quantity' ? 'By plates sold' : 'By revenue'}
          />
        </div>

        {/* ── three ways of splitting the same money ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14 }}>
          <OrderTypePanel rows={byType} orders={summary.orders} currency={currency} />
          <PaymentPanel
            rows={payment}
            currency={currency}
            note={
              summary.invoiceCount
                ? 'Money on account has already moved the customers’ balances.'
                : null
            }
          />
          <CategoryPanel rows={byCategory} currency={currency} />
        </div>

        {/* ── people, and the feed ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.55fr', gap: 14 }}>
          <StaffPanel rows={staff} currency={currency} />
          <LatestOrders
            orders={feed}
            currency={currency}
            onOpenOrders={() => navigate('/orderlist')}
          />
        </div>

        {/* ── only once there is more than one day to compare ── */}
        {!period.isSingleDay ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <WeekdayPanel
              rows={weekdays}
              currency={currency}
              note={weekdayNote(weekdays, currency)}
            />
            <NotMovingPanel data={idle} periodLabel={weekdayPeriodLabel(period)} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ── small pieces ───────────────────────────────────────────────────────── */

function Centered({ children }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 90px)',
        background: T.canvas,
        fontFamily: FONT_STACK,
      }}
    >
      {children}
    </div>
  );
}

function DateInput({ value, onChange }) {
  return (
    <input
      type="date"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={{
        height: 36,
        padding: '0 12px',
        background: T.surface,
        border: `1px solid ${T.borderStrong}`,
        borderRadius: T.radiusPill,
        font: 'inherit',
        fontFamily: FONT_STACK,
        fontSize: 12.5,
        color: T.ink,
      }}
    />
  );
}

function describeComparison(period) {
  return period.isSingleDay ? 'The same day last week' : 'The period before';
}

function weekdayPeriodLabel(period) {
  if (period.label === 'This month') return 'this month';
  if (period.label === 'This week') return 'this week';
  return 'in this period';
}

/**
 * One sentence about the weekday spread, and only when it says something.
 *
 * A five-percent gap between the best and worst day is noise; a shop that
 * takes twice as much on Saturday is a rota decision.
 */
function weekdayNote(rows, currency) {
  const withDays = rows.filter((row) => row.days > 0 && row.average > 0);
  if (withDays.length < 3) return null;

  const best = withDays.reduce((top, row) => (row.average > top.average ? row : top));
  const worst = withDays.reduce((low, row) => (row.average < low.average ? row : low));
  if (best.label === worst.label || worst.average <= 0) return null;

  const ratio = Math.round(((best.average - worst.average) / worst.average) * 100);
  if (ratio < 25) return null;

  return `${best.label} takes ${ratio}% more than ${worst.label} — ${money(
    best.average,
    currency
  )} against ${money(worst.average, currency)}.`;
}
